<template>
  <main class="meeting-tool">
    <section class="meeting-editor">
      <div class="container-fluid tool-container">
        <div class="tool-heading">
          <span>Toplantı aracı</span>
          <h1>Toplantı Notları ve Kararlar</h1>
          <p>Yönetim kurulu kararlarını, istişare notlarını ve önemli bilgilendirmeleri paylaşılabilir portre PNG olarak hazırlayın.</p>
        </div>

        <div class="tool-layout">
          <form class="tool-form" @submit.prevent="downloadAsPng">
            <div>
              <label for="title" class="form-label">Başlık</label>
              <input id="title" v-model="title" type="text" class="form-control" placeholder="Toplantı Notları ve Kararlar" />
            </div>

            <div>
              <label for="financeNotes" class="form-label">Mali Durum</label>
              <textarea id="financeNotes" v-model="financeNotes" class="form-control" rows="3" placeholder="Gelir, gider, aidat ve bağış durumu." />
            </div>

            <div>
              <label for="activityPlan" class="form-label">Aktivite Planı</label>
              <textarea id="activityPlan" v-model="activityPlan" class="form-control" rows="3" placeholder="Planlanan etkinlik ve çalışma başlıkları." />
            </div>

            <div>
              <label for="suggestions" class="form-label">Öneriler</label>
              <textarea id="suggestions" v-model="suggestions" class="form-control" rows="3" placeholder="Üyelerden gelen öneriler." />
            </div>

            <div>
              <label for="decisions" class="form-label">Alınan Kararlar</label>
              <textarea id="decisions" v-model="decisions" class="form-control" rows="4" placeholder="Her kararı ayrı satıra yazabilirsiniz." />
            </div>

            <div class="form-grid">
              <div>
                <label for="date" class="form-label">Tarih</label>
                <input id="date" v-model="date" type="text" class="form-control" placeholder="04.10.2025 Cumartesi 19:30" />
              </div>
              <div>
                <label for="place" class="form-label">Yer</label>
                <input id="place" v-model="place" type="text" class="form-control" placeholder="Opsiyonel" />
              </div>
            </div>

            <div>
              <label for="contact" class="form-label">İletişim</label>
              <input id="contact" v-model="contact" type="text" class="form-control" placeholder="Opsiyonel" />
            </div>

            <div class="tool-actions">
              <button type="submit" class="btn btn-primary">
                <i class="fa fa-download me-2"></i>PNG Olarak İndir
              </button>
            </div>
          </form>

          <div class="preview-shell">
            <div class="preview-note">
              <i class="fa fa-mobile-screen-button"></i>
              1080 x 1350 portre paylaşım formatı
            </div>

            <div ref="posterFrame" class="poster-scale">
              <div ref="captureArea" class="meeting-poster" :style="{ '--poster-scale': posterScale }">
                <div class="poster-band poster-band-top"></div>
                <div class="poster-band poster-band-bottom"></div>
                <div class="poster-mark"></div>

                <header class="poster-header">
                  <img
                    src="/img/logo.png"
                    alt="Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği Logosu"
                    class="poster-brand-logo"
                  >
                  <div>
                    <span>Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği</span>
                    <strong>Yönetim ve istişare bilgilendirmesi</strong>
                  </div>
                </header>

                <section class="poster-title">
                  <h2>{{ normalizedTitle }}</h2>
                </section>

                <section class="notes-card" :class="notesLengthClass">
                  <article v-for="section in visibleSections" :key="section.key" class="note-section">
                    <h3>
                      <i :class="section.icon"></i>
                      {{ section.title }}
                    </h3>
                    <ol v-if="section.items.length > 1">
                      <li v-for="(item, index) in section.items" :key="`${section.key}-${index}-${item}`">{{ item }}</li>
                    </ol>
                    <p v-else>{{ section.text }}</p>
                  </article>
                </section>

                <section v-if="infoCount" :class="['info-grid', `count-${infoCount}`]">
                  <div v-if="date.trim()">
                    <small>Tarih</small>
                    <span>
                      <i class="fa fa-calendar-days"></i>
                      <strong>{{ date }}</strong>
                    </span>
                  </div>
                  <div v-if="place.trim()">
                    <small>Yer</small>
                    <span>
                      <i class="fa fa-location-dot"></i>
                      <strong>{{ place }}</strong>
                    </span>
                  </div>
                  <div v-if="contact.trim()">
                    <small>İletişim</small>
                    <span>
                      <i class="fa fa-phone-alt"></i>
                      <strong>{{ contact }}</strong>
                    </span>
                  </div>
                </section>

                <footer class="poster-footer">
                  <img src="/img/logo.jpg" alt="Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği Logosu">
                  <div>
                    <span>Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği</span>
                    <strong>Kütük No: 06-158-130</strong>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import html2canvas from 'html2canvas'

const captureArea = ref(null)
const posterFrame = ref(null)
const posterScale = ref('0.52')
const title = ref('Toplantı Notları ve Kararlar')
const financeNotes = ref('Aidat ve bağış süreçlerinin daha düzenli takip edilmesi görüşüldü.')
const activityPlan = ref('Sosyal destek, taziye ve kültürel faaliyetler için çalışma grupları oluşturulması planlandı.')
const suggestions = ref("Ankara'da yaşayan Ağrılı hemşehrilerimize daha hızlı ulaşmak için iletişim ağının güçlendirilmesi önerildi.")
const decisions = ref(`Dernek faaliyetlerinin düzenli duyurulmasına karar verildi.
Bir sonraki toplantıya kadar ilçe temsilcileriyle görüşmeler yapılması kararlaştırıldı.`)
const date = ref('04.10.2025 Cumartesi 19:30')
const place = ref('Dernek Binası')
const contact = ref('0507 921 7404')

const parseItems = (value) => value
  .split('\n')
  .map((item) => item.replace(/^[-*•\d.)\s]+/, '').trim())
  .filter(Boolean)

const normalizedTitle = computed(() => title.value.trim() || 'Toplantı Notları ve Kararlar')
const sections = computed(() => [
  {
    key: 'finance',
    title: 'Mali Durum',
    icon: 'fa fa-chart-line',
    text: financeNotes.value.trim(),
    items: parseItems(financeNotes.value)
  },
  {
    key: 'activity',
    title: 'Aktivite Planı',
    icon: 'fa fa-calendar-check',
    text: activityPlan.value.trim(),
    items: parseItems(activityPlan.value)
  },
  {
    key: 'suggestions',
    title: 'Öneriler',
    icon: 'fa fa-lightbulb',
    text: suggestions.value.trim(),
    items: parseItems(suggestions.value)
  },
  {
    key: 'decisions',
    title: 'Alınan Kararlar',
    icon: 'fa fa-list-check',
    text: decisions.value.trim(),
    items: parseItems(decisions.value)
  }
])
const visibleSections = computed(() => {
  const active = sections.value.filter((section) => section.text)
  return active.length ? active : [{
    key: 'empty',
    title: 'Toplantı Notları',
    icon: 'fa fa-clipboard-list',
    text: 'Toplantıda alınan kararlar ve önemli bilgilendirmeler burada yer alacaktır.',
    items: []
  }]
})
const infoCount = computed(() => [date.value, place.value, contact.value].filter((item) => item.trim()).length)
const notesLengthClass = computed(() => {
  const length = visibleSections.value.reduce((total, section) => total + section.text.length, 0)
  const count = visibleSections.value.reduce((total, section) => total + Math.max(section.items.length, 1), 0)
  if (length > 1100 || count > 14) return 'tiny'
  if (length > 820 || count > 11) return 'very-long'
  if (length > 560 || count > 8) return 'long'
  if (length > 340 || count > 5) return 'medium'
  return 'short'
})

let resizeObserver

const updatePosterScale = () => {
  const width = posterFrame.value?.clientWidth || 560
  posterScale.value = String(Math.min(width / 1080, 1))
}

onMounted(() => {
  updatePosterScale()
  resizeObserver = new ResizeObserver(updatePosterScale)
  if (posterFrame.value) resizeObserver.observe(posterFrame.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

const downloadAsPng = async () => {
  if (!captureArea.value) return

  const previousTransform = captureArea.value.style.transform
  captureArea.value.style.transform = 'none'

  let canvas
  try {
    canvas = await html2canvas(captureArea.value, {
      backgroundColor: '#fffdf8',
      scale: 1,
      useCORS: true,
      width: 1080,
      height: 1350,
      windowWidth: 1080,
      windowHeight: 1350
    })
  } finally {
    captureArea.value.style.transform = previousTransform
  }

  const link = document.createElement('a')
  const today = new Date().toISOString().slice(0, 10)
  link.download = `ankara-agrililar-toplanti-kararlari-${today}.png`
  link.href = canvas.toDataURL('image/png')
  link.click()
}
</script>

<style scoped>
.meeting-tool {
  background: #f5f7fb;
  min-height: 100vh;
}

.meeting-editor {
  padding: 52px 0 88px;
}

.tool-container {
  max-width: 1760px;
  padding-left: 28px;
  padding-right: 28px;
}

.tool-heading {
  max-width: 900px;
  margin-bottom: 28px;
}

.tool-heading span {
  color: var(--primary);
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.tool-heading h1 {
  font-size: clamp(32px, 5vw, 54px);
  font-weight: 900;
  letter-spacing: 0;
  margin: 8px 0 10px;
}

.tool-heading p {
  color: #666565;
  font-size: 17px;
  margin: 0;
}

.tool-layout {
  display: grid;
  grid-template-columns: minmax(340px, 460px) minmax(720px, 1fr);
  gap: 28px;
  align-items: start;
}

.tool-form,
.preview-shell {
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(15, 23, 43, .08);
}

.tool-form {
  display: grid;
  gap: 18px;
  padding: 22px;
}

.tool-form label {
  color: var(--dark);
  font-weight: 800;
}

.tool-form .form-control {
  border-radius: 6px;
  min-height: 48px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.tool-actions {
  display: grid;
  gap: 10px;
}

.tool-actions .btn {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.preview-shell {
  padding: 18px;
  overflow: auto;
}

.preview-note {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #555;
  background: #f5f7fb;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  padding: 8px 12px;
  margin-bottom: 14px;
}

.preview-note i {
  color: var(--primary);
}

.poster-scale {
  width: min(100%, 720px);
  aspect-ratio: 4 / 5;
  margin: 0 auto;
}

.meeting-poster {
  position: relative;
  width: 1080px;
  height: 1350px;
  overflow: hidden;
  color: var(--dark);
  background: #fffdf8;
  transform: scale(var(--poster-scale));
  transform-origin: top left;
}

.poster-band,
.poster-mark {
  position: absolute;
  pointer-events: none;
}

.poster-band {
  left: 0;
  right: 0;
  height: 12px;
  background: linear-gradient(90deg, var(--primary) 0 34%, var(--dark) 34% 100%);
}

.poster-band-top {
  top: 0;
}

.poster-band-bottom {
  bottom: 0;
  background: linear-gradient(90deg, var(--dark) 0 68%, var(--primary) 68% 100%);
}

.poster-mark {
  width: 520px;
  height: 520px;
  right: -210px;
  top: 136px;
  border: 72px solid rgba(254, 161, 22, .08);
  border-radius: 50%;
}

.poster-header,
.poster-title,
.notes-card,
.info-grid,
.poster-footer {
  position: relative;
  z-index: 2;
}

.poster-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 62px 84px 0;
}

.poster-brand-logo {
  width: 132px;
  height: auto;
  flex: 0 0 132px;
  object-fit: contain;
}

.poster-header div {
  display: grid;
  gap: 5px;
}

.poster-header span {
  color: var(--dark);
  font-size: 20px;
  font-weight: 900;
  line-height: 1.18;
}

.poster-header strong {
  color: #666565;
  font-size: 15px;
}

.poster-title {
  padding: 58px 84px 0;
}

.poster-title h2 {
  max-width: 870px;
  color: var(--dark);
  font-size: 52px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 0;
}

.poster-title h2::after {
  content: "";
  display: block;
  width: 128px;
  height: 6px;
  background: var(--primary);
  margin-top: 20px;
  box-shadow: 34px 0 0 var(--dark);
}

.notes-card {
  height: 690px;
  display: grid;
  align-content: start;
  gap: 26px;
  margin: 42px 84px 0;
  background: #fff;
  border: 2px solid rgba(15, 23, 43, .08);
  border-top: 12px solid var(--primary);
  box-shadow: 0 22px 55px rgba(15, 23, 43, .08);
  padding: 42px 48px;
  overflow: hidden;
}

.note-section {
  display: grid;
  gap: 14px;
  border-bottom: 1px solid rgba(15, 23, 43, .08);
  padding-bottom: 22px;
}

.note-section:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.note-section h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--dark);
  font-size: 24px;
  font-weight: 900;
  line-height: 1.1;
  margin: 0;
}

.note-section h3 i {
  color: var(--primary);
  font-size: 22px;
}

.note-section ol {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: decision;
}

.note-section li {
  position: relative;
  color: #2f3340;
  font-size: 23px;
  font-weight: 750;
  line-height: 1.3;
  padding-left: 50px;
  counter-increment: decision;
}

.note-section li::before {
  content: counter(decision);
  position: absolute;
  left: 0;
  top: 1px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--dark);
  border-bottom: 4px solid var(--primary);
  font-size: 15px;
  font-weight: 900;
}

.note-section p {
  color: #2f3340;
  font-size: 23px;
  font-weight: 700;
  line-height: 1.34;
  text-align: left;
  white-space: pre-line;
  margin: 0;
}

.notes-card.short .note-section h3 {
  font-size: 25px;
}

.notes-card.medium {
  gap: 20px;
}

.notes-card.medium .note-section {
  gap: 11px;
  padding-bottom: 17px;
}

.notes-card.medium .note-section h3 {
  font-size: 21px;
}

.notes-card.medium .note-section li,
.notes-card.medium .note-section p {
  font-size: 20px;
  line-height: 1.28;
}

.notes-card.long {
  gap: 15px;
  padding: 34px 42px;
}

.notes-card.long .note-section {
  gap: 9px;
  padding-bottom: 13px;
}

.notes-card.long .note-section h3 {
  font-size: 19px;
}

.notes-card.long .note-section li,
.notes-card.long .note-section p {
  font-size: 18px;
  line-height: 1.24;
  padding-left: 42px;
}

.notes-card.very-long,
.notes-card.tiny {
  gap: 11px;
  padding: 28px 36px;
}

.notes-card.very-long .note-section,
.notes-card.tiny .note-section {
  gap: 7px;
  padding-bottom: 10px;
}

.notes-card.very-long .note-section h3 {
  font-size: 17px;
}

.notes-card.tiny .note-section h3 {
  font-size: 15px;
}

.notes-card.very-long .note-section li,
.notes-card.very-long .note-section p {
  font-size: 15px;
  line-height: 1.22;
  padding-left: 36px;
}

.notes-card.tiny .note-section li,
.notes-card.tiny .note-section p {
  font-size: 12px;
  line-height: 1.18;
  padding-left: 32px;
}

.notes-card.long .note-section li::before,
.notes-card.very-long .note-section li::before,
.notes-card.tiny .note-section li::before {
  width: 26px;
  height: 26px;
  border-bottom-width: 3px;
  font-size: 12px;
}

.info-grid {
  position: absolute;
  left: 84px;
  right: 84px;
  bottom: 136px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.info-grid.count-1 {
  grid-template-columns: minmax(0, 1fr);
}

.info-grid.count-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-grid div {
  min-height: 106px;
  display: grid;
  grid-template-rows: 22px 1fr;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .1);
  border-bottom: 5px solid var(--primary);
  padding: 14px 18px 16px;
}

.info-grid small {
  color: #6b7280;
  font-size: 15px;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
}

.info-grid span {
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--dark);
  text-align: center;
}

.info-grid i {
  color: var(--primary);
  font-size: 24px;
}

.info-grid strong {
  color: var(--dark);
  font-size: 20px;
  font-weight: 900;
  line-height: 1.18;
}

.poster-footer {
  position: absolute;
  left: 84px;
  right: 84px;
  bottom: 40px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #666565;
  border-top: 1px solid rgba(15, 23, 43, .12);
  padding-top: 22px;
  font-size: 17px;
}

.poster-footer img {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  border-radius: 50%;
  object-fit: cover;
}

.poster-footer div {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.poster-footer strong {
  color: var(--dark);
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .meeting-editor {
    padding: 36px 0 96px;
  }

  .tool-layout,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .tool-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .preview-shell {
    padding: 12px;
  }
}
</style>
